import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Inject, OnInit } from '@angular/core';
import { SupportService } from '../support.service';
import { UserServiceInterface } from '@hutsix/ngxh6';
import { environment } from '../../../../environments/environment';

export type WidgetView = 'closed' | 'list' | 'new' | 'detail';

@Component({
    selector: 'app-support-widget',
    templateUrl: './support-widget.component.html',
    styleUrls: ['./support-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class SupportWidgetComponent implements OnInit {
    view: WidgetView = 'closed';
    selectedTicketKey: string | null = null;
    enabled = false;

    // Admin connection state
    isAdmin = false;
    connected: boolean | null = null; // null = loading
    connectDescriptorUrl = '';
    authMethod: string | null = null;

    constructor(
        public supportService: SupportService,
        @Inject('UserService') private userService: UserServiceInterface,
        private cdr: ChangeDetectorRef,
        private el: ElementRef,
    ) {}

    ngOnInit(): void {
        this.enabled = this.supportService.isEnabled;
        this.isAdmin = this.userService.user?.email === 'team@hutsix.com.au';
        this.connectDescriptorUrl = environment.apiUrl + '/atlassian-connect.json';

        // Apply theme color from environment config
        const color = (environment as any).support?.primaryColor;
        if (color) {
            const host = this.el.nativeElement as HTMLElement;
            host.style.setProperty('--sw-primary', color);
            host.style.setProperty('--sw-primary-hover', this.adjustBrightness(color, 20));
        }
    }

    private adjustBrightness(hex: string, amount: number): string {
        const num = parseInt(hex.replace('#', ''), 16);
        const r = Math.min(255, ((num >> 16) & 0xff) + amount);
        const g = Math.min(255, ((num >> 8) & 0xff) + amount);
        const b = Math.min(255, (num & 0xff) + amount);
        return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`;
    }

    toggle(): void {
        if (this.view === 'closed') {
            this.view = 'list';
            this.selectedTicketKey = null;
            // Check connection status when admin opens the panel
            if (this.isAdmin && this.connected === null) {
                this.checkConnection();
            }
        } else {
            this.view = 'closed';
        }
    }

    close(): void {
        this.view = 'closed';
        this.selectedTicketKey = null;
    }

    showNewTicket(): void {
        this.view = 'new';
    }

    showList(): void {
        this.view = 'list';
        this.selectedTicketKey = null;
    }

    showDetail(issueKey: string): void {
        this.selectedTicketKey = issueKey;
        this.view = 'detail';
    }

    onTicketCreated(): void {
        this.view = 'list';
        this.cdr.markForCheck();
    }

    // Admin: check if Jira is connected
    checkConnection(): void {
        this.supportService.checkConnection().subscribe({
            next: (res: any) => {
                this.connected = res.connected ?? false;
                this.authMethod = res.authMethod ?? 'connect';
                this.cdr.markForCheck();
            },
            error: () => {
                this.connected = false;
                this.authMethod = null;
                this.cdr.markForCheck();
            },
        });
    }
}
